import { useState, useEffect, useCallback } from 'react';
import { initiateOutboundCall } from '../../services/outboundCalls';

/**
 * Returns true if the appointment's preferred time is a wide range (e.g., full day).
 * When this is the case, providing alternative times becomes mandatory.
 *
 * A "wide range" is defined as more than 1 hour difference between start and end.
 * If the range is 1 hour or less (e.g., "12:00 PM to 1:00 PM"), it's considered
 * a specific time slot and alternatives are optional.
 */
function isTimeRangeAppointment(appointment) {
  const dateRanges = appointment?.date_ranges;
  if (!dateRanges || !Array.isArray(dateRanges) || dateRanges.length === 0) {
    return false;
  }
  const first = dateRanges[0];
  if (!first?.start || !first?.end || first.start === first.end) {
    return false;
  }

  try {
    // Parse the start and end times
    const startDate = new Date(first.start.replace(' ', 'T'));
    const endDate = new Date(first.end.replace(' ', 'T'));

    // Calculate difference in hours
    const diffMs = endDate - startDate;
    const diffHours = diffMs / (1000 * 60 * 60);

    // If the range is more than 1 hour, it's considered a "wide range"
    // and alternatives are mandatory
    return diffHours > 1;
  } catch {
    // If parsing fails, fall back to simple string comparison
    return first.start !== first.end;
  }
}

/**
 * Modal for initiating outbound appointment confirmation calls.
 * Supports both simple confirmation and confirmation with alternative times.
 */
export default function OutboundCallModal({
  isOpen,
  onClose,
  appointment,
  onCallInitiated,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [useAlternatives, setUseAlternatives] = useState(false);
  const [alternativeTimes, setAlternativeTimes] = useState([
    { date: '', time: '' },
    { date: '', time: '' },
    { date: '', time: '' },
  ]);
  const [language, setLanguage] = useState('en');

  // Supported languages for outbound calls
  const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Spanish' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
    { code: 'it', label: 'Italian' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'tr', label: 'Turkish' },
    { code: 'zh', label: 'Chinese' },
    { code: 'ja', label: 'Japanese' },
    { code: 'ko', label: 'Korean' },
    { code: 'ar', label: 'Arabic' },
    { code: 'hi', label: 'Hindi' },
    { code: 'vi', label: 'Vietnamese' },
  ];

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setIsLoading(false);
      // If the appointment has a time range, alternatives are mandatory — force the toggle on
      setUseAlternatives(isTimeRangeAppointment(appointment));
      setAlternativeTimes([
        { date: '', time: '' },
        { date: '', time: '' },
        { date: '', time: '' },
      ]);
      setLanguage('en');
    }
  }, [isOpen, appointment]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isLoading) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) onClose();
  };

  const handleAlternativeChange = (index, field, value) => {
    setAlternativeTimes((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Check if alternative times have incomplete entries (date without time or vice versa)
  const getAlternativeTimesValidation = useCallback(() => {
    if (!useAlternatives) return { isValid: true, error: null };

    const incompleteEntries = alternativeTimes.filter(
      (alt) => (alt.date && !alt.time) || (!alt.date && alt.time)
    );

    if (incompleteEntries.length > 0) {
      return {
        isValid: false,
        error: 'Please provide both date and time for each alternative slot, or leave both empty.',
      };
    }

    // When the appointment has a time range, at least one complete alternative is required
    const isTimeRange = isTimeRangeAppointment(appointment);
    if (isTimeRange) {
      const completeEntries = alternativeTimes.filter((alt) => alt.date && alt.time);
      if (completeEntries.length === 0) {
        return {
          isValid: false,
          error: 'At least one alternative time slot is required because the preferred time is a range.',
        };
      }
    }

    return { isValid: true, error: null };
  }, [useAlternatives, alternativeTimes, appointment]);

  const formatAlternativeTimesForApi = useCallback(() => {
    if (!useAlternatives) return null;

    const formatted = alternativeTimes
      .filter((alt) => alt.date && alt.time)
      .map((alt) => {
        // Combine date and time into ISO format
        const startDateTime = `${alt.date} ${alt.time}:00`;
        // Default end time is 30 min after start (can be adjusted)
        const [hours, minutes] = alt.time.split(':').map(Number);
        const endMinutes = minutes + 30;
        const endHours = hours + Math.floor(endMinutes / 60);
        const endTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
        const endDateTime = `${alt.date} ${endTime}:00`;

        return { start: startDateTime, end: endDateTime };
      });

    return formatted.length > 0 ? formatted : null;
  }, [useAlternatives, alternativeTimes]);

  // Get current validation state
  const alternativesValidation = getAlternativeTimesValidation();

  const handleSubmit = async () => {
    // Validate alternative times first
    if (!alternativesValidation.isValid) {
      setError(alternativesValidation.error);
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const alternatives = formatAlternativeTimesForApi();
      console.log('[OutboundCall] Initiating call for appointment:', appointment.id);
      console.log('[OutboundCall] Alternatives:', alternatives);
      console.log('[OutboundCall] Language:', language);

      const callData = await initiateOutboundCall(appointment.id, alternatives, language);
      console.log('[OutboundCall] Response received:', callData);

      if (callData && callData.id) {
        console.log('[OutboundCall] Call initiated successfully, id:', callData.id);
        onCallInitiated?.(callData);
        onClose();
      } else {
        console.error('[OutboundCall] No call data returned:', callData);
        setError('Failed to initiate call - no call data returned');
      }
    } catch (err) {
      console.error('[OutboundCall] Error initiating call:', err);
      setError(err.message || 'Failed to initiate outbound call');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const patientName = appointment?.patient_name || 'Unknown Patient';
  const phoneNumber = appointment?.phone_number || appointment?.callback_phone_number;
  const appointmentTime = formatAppointmentTime(appointment?.date_ranges);
  const isTimeRange = isTimeRangeAppointment(appointment);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={handleBackdropClick}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Confirm Appointment Call
                </h3>
                <p className="mt-1 text-sm text-blue-100">
                  {patientName}
                  {phoneNumber && (
                    <span className="ml-2 opacity-80">{phoneNumber}</span>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="rounded-lg p-1.5 text-blue-100 hover:bg-white/10 hover:text-white transition-colors disabled:opacity-50"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Call Preview */}
            <div className="mb-5 rounded-lg bg-gray-50 p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Call Preview
              </h4>
              <p className="text-sm text-gray-600">
                The AI will call <strong>{patientName}</strong> to confirm their
                appointment
                {appointmentTime && (
                  <span>
                    {' '}
                    scheduled for <strong>{appointmentTime}</strong>
                  </span>
                )}
                .
              </p>
            </div>

            {/* Language Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Call Language
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                The AI agent will speak in this language during the call.
              </p>
            </div>

            {/* Time Range Required Notice */}
            {isTimeRange && (
              <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 p-3">
                <div className="flex items-start gap-2">
                  <svg className="h-4 w-4 mt-0.5 shrink-0 text-orange-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-orange-800">Alternative times required</p>
                    <p className="mt-0.5 text-xs text-orange-700">
                      The preferred time is a range. You must provide at least one specific alternative time slot for the AI to offer the patient.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Alternative Times Toggle */}
            <div className="mb-4">
              <label className={`flex items-center gap-3 ${isTimeRange ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={useAlternatives}
                  onChange={(e) => !isTimeRange && setUseAlternatives(e.target.checked)}
                  disabled={isTimeRange}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                />
                <span className="text-sm font-medium text-gray-700">
                  Offer alternative times{isTimeRange && <span className="ml-1 text-orange-600 font-semibold">*</span>}
                </span>
              </label>
              <p className="mt-1 ml-7 text-xs text-gray-500">
                The AI will offer these times if the patient can't make the original appointment.
              </p>
            </div>

            {/* Alternative Times Inputs */}
            {useAlternatives && (
              <div className={`space-y-3 rounded-lg border p-4 ${
                !alternativesValidation.isValid
                  ? 'border-amber-300 bg-amber-50/50'
                  : 'border-gray-200'
              }`}>
                <h5 className="text-sm font-medium text-gray-700">
                  Alternative Time Slots
                </h5>
                {alternativeTimes.map((alt, index) => {
                  const isIncomplete = (alt.date && !alt.time) || (!alt.date && alt.time);
                  return (
                    <div key={index} className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">
                          Date {index + 1}
                        </label>
                        <input
                          type="date"
                          value={alt.date}
                          onChange={(e) =>
                            handleAlternativeChange(index, 'date', e.target.value)
                          }
                          min={new Date().toISOString().split('T')[0]}
                          className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 ${
                            isIncomplete && !alt.date
                              ? 'border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-500/20'
                              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">
                          Time {index + 1}
                        </label>
                        <input
                          type="time"
                          value={alt.time}
                          onChange={(e) =>
                            handleAlternativeChange(index, 'time', e.target.value)
                          }
                          className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 ${
                            isIncomplete && !alt.time
                              ? 'border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-500/20'
                              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                          }`}
                        />
                      </div>
                    </div>
                  );
                })}
                {!alternativesValidation.isValid ? (
                  <p className="text-xs text-amber-700 flex items-center gap-1">
                    <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    {alternativesValidation.error}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">
                    {isTimeRange
                      ? 'At least one slot is required. Leave unused slots blank.'
                      : 'Leave blank any slots you don\'t want to offer.'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || !phoneNumber || !alternativesValidation.isValid}
              className="inline-flex items-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
            >
              {isLoading ? (
                <>
                  <svg
                    className="h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Initiating Call...
                </>
              ) : (
                <>
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  Start Call
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Format date_ranges to human-readable string
 */
function formatAppointmentTime(dateRanges) {
  if (!dateRanges || dateRanges.length === 0) return null;

  const first = dateRanges[0];
  if (!first.start) return null;

  try {
    const date = new Date(first.start.replace(' ', 'T'));
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return first.start;
  }
}
